/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import axiosInstance from '../functions/AxiosInstance'; // Asegúrate de que la ruta sea correcta



// Función para registrar la visualización de un activo digital
const registerDigitalAssetView = async ({ id }: { assetId: string }) => {
  try {
    const response = await axiosInstance.post(`/digital-assets/view/${id}`);
    return response.data; // Devuelve los datos de la respuesta sin lanzar notificación
  } catch (error: any) {
    const errorMessage = error.response?.data?.message || 'Error desconocido';
    console.error('Error al registrar la visualización:', errorMessage); // Solo registramos el error en consola sin mostrar notificación
  }
};

// Hook para registrar la visualización de un activo digital
export const useRegisterDigitalAssetView = () => {
  const mutation = useMutation({
    mutationFn: registerDigitalAssetView,
    onSuccess: () => {
      // Eliminar notificación de éxito
    },
    onError: () => {
      // Eliminar notificación de error
    },
  });

  return {
    ...mutation,
    isLoading: mutation.isPending, // Estado de carga
    isSuccess: mutation.isSuccess, // Indica si la acción fue exitosa
    error: mutation.error?.message || '', // Captura el error correctamente
  };
};



// Función para crear un activo digital
const createDigitalAsset = async (formData: FormData) => {
  try {
    const response = await axiosInstance.post('digital-assets/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data; // Devuelve los datos de la respuesta
  } catch (error: any) {
    const errorMessage = error.response?.data?.message || 'Error desconocido';
    toast.error(`Error al crear el activo digital: ${errorMessage}`);
    console.error('Error al crear el activo digital:', errorMessage);
    throw new Error(errorMessage); // Lanza el error para manejarlo en el hook
  }
};

// Hook para crear un activo digital
export const useCreateDigitalAsset = () => {
  const mutation = useMutation({
    mutationFn: createDigitalAsset,
    onSuccess: (data) => {
      toast.success(data?.message || 'Activo digital creado con éxito.');
    },
    onError: (error: any) => {
      const errorMessage = error?.message || 'Error desconocido';
      console.error('Error al crear el activo digital:', errorMessage);
      toast.error(errorMessage);
    },
  });

  return {
    ...mutation,
    isLoading: mutation.isPending, // Estado de carga
    isSuccess: mutation.isSuccess, // Indica si la acción fue exitosa
    error: mutation.error?.message || '', // Captura el error correctamente
  };
};



// Función para activar un activo digital
const activateDigitalAsset = async (id: string) => {
  try {
    const response = await axiosInstance.put(`/digital-assets/activate/${id}`);
    return response.data; // Devuelve los datos de la respuesta
  } catch (error: any) {
    const errorMessage = error.response?.data?.message || 'Error desconocido';
    toast.error(`Error al activar el activo digital: ${errorMessage}`);
    console.error('Error al activar el activo digital:', errorMessage);
    throw new Error(errorMessage); // Lanza el error para manejarlo en el hook
  }
};

// Hook para activar un activo digital
export const useActivateDigitalAsset = () => {
  const mutation = useMutation({
    mutationFn: activateDigitalAsset,
    onSuccess: (data) => {
      toast.success(data?.message || 'Activo digital activado con éxito.');
    },
    onError: (error: any) => {
      const errorMessage = error?.message || 'Error desconocido';
      console.error('Error al activar el activo digital:', errorMessage);
      toast.error(errorMessage);
    },
  });

  return {
    ...mutation,
    isLoading: mutation.isPending, // Estado de carga
    isSuccess: mutation.isSuccess, // Indica si la acción fue exitosa
    error: mutation.error?.message || '', // Captura el error correctamente
  };
};


// Función para obtener los activos digitales del vendedor
const fetchDigitalAssetsBySeller = async () => {
  try {
    const response = await axiosInstance.get('/digital-assets/seller');
    return response.data; // Devuelve los datos de la respuesta
  } catch (error: any) {
    const errorMessage = error.response?.data?.message || 'Error desconocido';
    toast.error(`Error al obtener los activos digitales: ${errorMessage}`);
    console.error('Error al obtener los activos digitales:', errorMessage);
    throw new Error(errorMessage); // Lanza el error para manejarlo en el hook
  }
};

// Hook para obtener los activos digitales del vendedor
export const useGetDigitalAssetsBySeller = () => {
  const { data, error, isLoading, isError, isSuccess, refetch } = useQuery({
    queryKey: ['digitalAssetsBySeller'], // Clave única para React Query
    queryFn: fetchDigitalAssetsBySeller, // Función de consulta
    refetchOnWindowFocus: false, // Evita que se vuelva a cargar al cambiar de pestaña
  });

  return {
    digitalAssets: data, // Lista de activos digitales del vendedor
    isLoading, // Indica si está cargando
    isError, // Indica si hubo un error
    error, // Información del error
    isSuccess, // Indica si la consulta fue exitosa
    refetch, // Función para volver a cargar los datos
  };
};



// Función para obtener los activos digitales públicos
const fetchPublicDigitalAssets = async () => {
  try {
    const response = await axiosInstance.get('/digital-assets/public/');
    return response.data; // Devuelve los datos obtenidos
  } catch (error: any) {
    const errorMessage = error.response?.data?.message || 'Error desconocido';
    toast.error(`Error al obtener los activos digitales públicos: ${errorMessage}`);
    console.error('Error al obtener los activos digitales públicos:', errorMessage);
    throw new Error(errorMessage); // Lanza el error para manejarlo en el hook
  }
};

// Hook para obtener los activos digitales públicos
export const useGetPublicDigitalAssets = () => {
  const { data, error, isLoading, isError, isSuccess, refetch } = useQuery({
    queryKey: ['publicDigitalAssets'], // Clave única para React Query
    queryFn: fetchPublicDigitalAssets, // Función de consulta
    refetchOnWindowFocus: false, // Evita recargar al cambiar de pestaña
  });

  return {
    publicDigitalAssets: data, // Lista de activos digitales públicos
    isLoading, // Indica si está cargando
    isError, // Indica si hubo un error
    error, // Información del error
    isSuccess, // Indica si la consulta fue exitosa
    refetch, // Función para volver a cargar los datos
  };
};




// Función para obtener los activos digitales en estado "draft"
const fetchDraftDigitalAssets = async () => {
  try {
    const response = await axiosInstance.get('/digital-assets/draft');
    return response.data; // Devuelve los datos obtenidos
  } catch (error: any) {
    const errorMessage = error.response?.data?.message || 'Error desconocido';
    toast.error(`Error al obtener los activos digitales en borrador: ${errorMessage}`);
    console.error('Error al obtener los activos digitales en borrador:', errorMessage);
    throw new Error(errorMessage); // Lanza el error para manejarlo en el hook
  }
};

// Hook para obtener los activos digitales en estado "draft"
export const useGetDraftDigitalAssets = () => {
  const { data, error, isLoading, isError, isSuccess, refetch } = useQuery({
    queryKey: ['draftDigitalAssets'], // Clave única para React Query
    queryFn: fetchDraftDigitalAssets, // Función de consulta
    refetchOnWindowFocus: false, // Evita recargar al cambiar de pestaña
  });

  return {
    draftDigitalAssets: data, // Lista de activos digitales en estado "draft"
    isLoading, // Indica si está cargando
    isError, // Indica si hubo un error
    error, // Información del error
    isSuccess, // Indica si la consulta fue exitosa
    refetch, // Función para volver a cargar los datos
  };
};






// Función para obtener un activo digital por ID
const fetchDigitalAssetById = async (id: string) => {
  const response = await axiosInstance.get(`/digital-assets/digital/details/${id}`);
  return response.data; // Devuelve los datos obtenidos
};

// Hook tipificado para obtener un activo digital por ID
export const useGetDigitalAssetById = (id: string) => {
  const { data, error, isLoading, isError } = useQuery({
    queryKey: ['digitalAsset', id], // Identifica la consulta con el ID del activo digital
    queryFn: async () => {
      try {
        return await fetchDigitalAssetById(id); // Llama a la función con el ID
      } catch (error) {
        const errorMessage = (error as any).response?.data?.message || 'Error desconocido';
        toast.error(`Error al obtener el activo digital: ${errorMessage}`);
        console.error('Error al obtener el activo digital:', error);
        throw error; // Lanza el error para manejarlo en el hook
      }
    },
    refetchOnWindowFocus: false, // Evita que se recarguen los datos al cambiar de ventana
  });

  return {
    digitalAsset: data,      // El activo digital obtenido
    isLoading,               // Indica si está cargando
    isError,                 // Indica si hubo un error
    error,                   // Información del error
  };
};



// Función para actualizar el estado de un activo digital
const updateDigitalAssetStatus = async ({ id, status }: { id: string; status: string }) => {
  try {
    const response = await axiosInstance.put(`/digital-assets/${id}/status`, { status });
    return response.data; // Devuelve los datos de la respuesta
  } catch (error: any) {
    const errorMessage = error.response?.data?.message || 'Error desconocido';
    toast.error(`Error al actualizar el estado del activo digital: ${errorMessage}`);
    console.error('Error al actualizar el estado del activo digital:', errorMessage);
    throw new Error(errorMessage); // Lanza el error para manejarlo en el hook
  }
};

// Hook para actualizar el estado de un activo digital
export const useUpdateDigitalAssetStatus = () => {
  const mutation = useMutation({
    mutationFn: updateDigitalAssetStatus,
    onSuccess: (data) => {
      toast.success(data?.message || 'Estado del activo digital actualizado con éxito.');
    },
    onError: (error: any) => {
      // Captura el mensaje de error exacto enviado por el servidor
      const errorMessage = error?.message || 'Error desconocido';
      console.error('Error al actualizar el estado del activo digital:', errorMessage);
      toast.error(errorMessage);
    },
  });

  return {
    ...mutation,
    isLoading: mutation.isPending, // Estado de carga de la mutación
    isSuccess: mutation.isSuccess, // Indica si la actualización fue exitosa
    error: mutation.error?.message || '', // Captura el error correctamente
  };
};


const addDigitalAssetToCart = async ({ digitalAssetId }: { digitalAssetId: string }) => {
  const response = await axiosInstance.post('/digital-assets/cart/add', { digitalAssetId });
  return response.data; // Devuelve los datos de la respuesta
};

export const useAddDigitalAssetToCart = () => {
  const mutation = useMutation({
    mutationFn: addDigitalAssetToCart,
    onSuccess: (data) => {
      toast.success(data?.message || 'Activo digital agregado al carrito con éxito.');
    },
    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        'Error desconocido';

      toast.error(errorMessage);
      console.error('Error al agregar el activo digital al carrito:', errorMessage);
    },
  });

  return {
    ...mutation,
    isLoading: mutation.isPending,
    isSuccess: mutation.isSuccess,
    error: mutation.error?.message || '',
  };
};



// Función para eliminar un activo digital
const deleteDigitalAsset = async ({ id }: { id: string }) => {
  try {
    const response = await axiosInstance.delete(`digital-assets/${id}`);
    return response.data; // Devuelve los datos de la respuesta
  } catch (error: any) {
    const errorMessage = error.response?.data?.message || 'Error desconocido';
    toast.error(`Error al eliminar el activo digital: ${errorMessage}`);
    console.error('Error al eliminar el activo digital:', errorMessage);
    throw new Error(errorMessage); // Lanza el error para manejarlo en el hook
  }
};

// Hook para eliminar un activo digital
export const useDeleteDigitalAsset = () => {
  const mutation = useMutation({
    mutationFn: deleteDigitalAsset,
    onSuccess: (data) => {
      toast.success(data?.message || 'Activo digital eliminado con éxito.');
    },
    onError: (error: any) => {
      // Captura el mensaje de error exacto enviado por el servidor
      const errorMessage = error?.message || 'Error desconocido';
      console.error('Error al eliminar el activo digital:', errorMessage);
      toast.error(errorMessage);
    },
  });

  return {
    ...mutation,
    isLoading: mutation.isPending, // Estado de carga de la mutación
    isSuccess: mutation.isSuccess, // Indica si la eliminación fue exitosa
    error: mutation.error?.message || '', // Captura el error correctamente
  };
};





